import React, { useState, useEffect } from 'react';
import { usePushNotifications } from '../../hooks/features/usePushNotifications';

// Detect if running as installed PWA (required for iOS push)
function isInstalledPWA(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as any).standalone === true
  );
}

// Detect iOS
function isIOS(): boolean {
  return /iphone|ipad|ipod/i.test(navigator.userAgent) && 'ontouchend' in document;
}

interface PushNotificationModalProps {
  onClose: () => void;
}

export const PushNotificationModal: React.FC<PushNotificationModalProps> = ({ onClose }) => {
  const [status, setStatus] = useState<'idle' | 'granted' | 'denied' | 'ios-not-installed'>('idle');
  const ios = isIOS();
  const installedPWA = isInstalledPWA();
  const { subscribe, isLoading } = usePushNotifications({ uid: 'temp' }); // user doesn't matter for specific permission request

  // On mount: if iOS but not installed as PWA, show install prompt instead
  useEffect(() => {
    if (ios && !installedPWA) {
      setStatus('ios-not-installed');
    }
  }, [ios, installedPWA]);

  const handleEnable = async () => {
    const success = await subscribe();
    if (success) {
      setStatus('granted');
      setTimeout(onClose, 1400);
    } else {
      const permission = Notification.permission;
      if (permission === 'denied') {
        setStatus('denied');
        setTimeout(onClose, 1200);
      }
    }
  };

  const handleDismiss = () => {
    onClose();
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        padding: '0 16px 32px',
        background: 'rgba(0,0,0,0.55)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        animation: 'fadeInBackdrop 0.3s ease forwards',
      }}
      onClick={handleDismiss}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '480px',
          background: 'rgba(13, 17, 28, 0.95)',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: '24px',
          padding: '28px 24px',
          boxShadow: '0 24px 64px rgba(0,0,0,0.6), 0 0 0 1px rgba(199,47,56,0.15)',
          animation: 'slideUp 0.4s cubic-bezier(0.16,1,0.3,1) forwards',
          backdropFilter: 'blur(32px)',
          WebkitBackdropFilter: 'blur(32px)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Icon + Title */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '16px' }}>
          <div style={{
            width: '52px', height: '52px', borderRadius: '16px',
            background: 'linear-gradient(135deg, #c72f38, #7e161c)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
            boxShadow: '0 8px 20px rgba(199,47,56,0.35)',
          }}>
            <i className="bi bi-bell-fill" style={{ fontSize: '1.4rem', color: '#fff' }} />
          </div>
          <div>
            <div style={{ fontSize: '1rem', fontWeight: 800, color: '#fff', lineHeight: 1.2 }}>
              {status === 'ios-not-installed' ? 'Add to Home Screen First' : 'Stay in the loop'}
            </div>
            <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.45)', marginTop: '3px', fontWeight: 500 }}>
              ATL Card Sharks notifications
            </div>
          </div>
        </div>

        {/* Body content based on state */}
        {status === 'idle' && (
          <p style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.6)', lineHeight: 1.6, margin: '0 0 20px' }}>
            Get instant alerts for new messages, event reminders, and Shark Lair activity
            {ios ? ' — even when your phone is locked.' : ' — even when the app is closed.'}
          </p>
        )}

        {/* iOS not installed prompt */}
        {status === 'ios-not-installed' && (
          <>
            <p style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.6)', lineHeight: 1.6, margin: '0 0 16px' }}>
              Push notifications on iPhone require the app to be installed to your home screen.
            </p>
            <div style={{
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: '14px',
              padding: '14px 16px',
              marginBottom: '20px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                <span style={{ fontSize: '1.2rem' }}>1️⃣</span>
                <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.7)', fontWeight: 600 }}>
                  Tap the <strong style={{ color: '#fff' }}>Share</strong> button in Safari
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                <span style={{ fontSize: '1.2rem' }}>2️⃣</span>
                <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.7)', fontWeight: 600 }}>
                  Tap <strong style={{ color: '#fff' }}>"Add to Home Screen"</strong>
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '1.2rem' }}>3️⃣</span>
                <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.7)', fontWeight: 600 }}>
                  Open the app from your home screen and enable notifications
                </span>
              </div>
            </div>
          </>
        )}

        {status === 'granted' && (
          <p style={{ fontSize: '0.875rem', color: '#6ee7b7', lineHeight: 1.6, margin: '0 0 24px', fontWeight: 700 }}>
            <i className="bi bi-check-circle-fill" style={{ marginRight: '8px' }} />
            Notifications enabled! You'll hear from the Lair.
          </p>
        )}

        {status === 'denied' && (
          <p style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.45)', lineHeight: 1.6, margin: '0 0 24px' }}>
            No problem — you can enable notifications anytime from your browser or device settings.
          </p>
        )}

        {/* Actions */}
        {(status === 'idle') && (
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={handleDismiss}
              style={{
                flex: 1, padding: '13px', borderRadius: '12px',
                background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)',
                color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem', fontWeight: 700,
                cursor: 'pointer', letterSpacing: '0.04em',
              }}
            >
              Not Now
            </button>
            <button
              onClick={handleEnable}
              disabled={isLoading}
              style={{
                flex: 2, padding: '13px', borderRadius: '12px',
                background: isLoading ? 'rgba(199,47,56,0.5)' : 'linear-gradient(135deg, #c72f38, #9f1f2a)',
                border: 'none', color: '#fff', fontSize: '0.8rem', fontWeight: 800,
                cursor: isLoading ? 'not-allowed' : 'pointer', letterSpacing: '0.06em',
                textTransform: 'uppercase', boxShadow: '0 4px 16px rgba(199,47,56,0.3)',
                transition: 'all 0.2s ease',
              }}
            >
              {isLoading ? 'Enabling...' : 'Enable Notifications'}
            </button>
          </div>
        )}

        {status === 'ios-not-installed' && (
          <button
            onClick={handleDismiss}
            style={{
              width: '100%', padding: '13px', borderRadius: '12px',
              background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
              color: 'rgba(255,255,255,0.6)', fontSize: '0.8rem', fontWeight: 700,
              cursor: 'pointer', letterSpacing: '0.04em',
            }}
          >
            Got It
          </button>
        )}
      </div>

      <style>{`
        @keyframes fadeInBackdrop { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(40px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
};
